import { Component, Input, OnInit, ViewChild, ElementRef, QueryList, Output, EventEmitter } from '@angular/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { filter as _filter } from 'lodash';
import { fromEvent } from 'rxjs';

import { AutocompleteOption } from '../../interfaces/autocomplete-option.interface';
import { CommonValues } from '../../classes/common-values';
import { ValidationMessage } from '../../classes/validation-message';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'nsc-form-field-autocomplete',
  templateUrl: './form-field-autocomplete.component.html',
  styleUrls: ['./form-field-autocomplete.component.scss'],
  standalone: false
})
export class FormFieldAutocompleteComponent implements OnInit {
  @Input() controlName: string;
  @Input() group: FormGroup;
  @Input() hint: string;
  @Input() idx: string;
  @Input() notInList: boolean;
  @Input() options: AutocompleteOption[];
  @Input() placeholder: string;
  @Input() loading = false;
  @Input() searchType = this.commonValues.searchType.startsWithSearch;
  @Input() minCharactersToTrigger = 2;

  @Output() valueSelected = new EventEmitter<any>();

  @ViewChild('itemFilterInput', { static: true }) itemFilterInput: ElementRef;
  @ViewChild('optionList', { read: ElementRef })
  optionList: ElementRef;

  focusTimeout: number;
  focusTimeoutDelay = 250;
  highlightedItem = 0;
  inputTimeout: number;
  inputTimeoutDelay: 300;
  show = {
    autocomplete: false
  };

  values = {
    autocomplete: [], // options to display to the user;
    hint: ''
  };

  collapsedLiveText = '';
  private lastAnnouncedAccessibleName = '';

  constructor(private commonValues: CommonValues,
    private validationMessage: ValidationMessage,
    private liveAnnouncer: LiveAnnouncer) { }

  ngOnInit() {
    this.setHint(this.hint);
    this.configureAutocomplete();
  }

  setHint(hint: string, required?: boolean): void {
    // if a field isn't required and it's optional, we need to combine the `hint` values;
    this.values.hint = `${hint || ''}`;
  }

  configureAutocomplete(): void {
    fromEvent(this.itemFilterInput.nativeElement, 'keyup')
      .pipe(distinctUntilChanged())
      .subscribe((e: KeyboardEvent) => {
        this.keyUpHandler(e);
      });

  }

  showOptions(): void {
    // I removed 'debounce' from the Event Observable and I am replicating it's behavior
    // with this inputTimeout timer object
    window.clearTimeout(this.inputTimeout);
    const value = this.itemFilterInput.nativeElement.value;
    this.highlightedItem = 0;
    if (value && value.length >= this.minCharactersToTrigger) {
      this.values.autocomplete = _filter(this.options, (option: AutocompleteOption) => {
        // create a list where the entered value matchs something in the name;
        if (this.searchType === this.commonValues.searchType.wildCardSearch) {
          return option.schlName.toLowerCase().indexOf(value.toLowerCase()) > -1;
        } else if (this.searchType === this.commonValues.searchType.startsWithSearch) {
          return option.schlName.toLowerCase().indexOf(value.toLowerCase()) === 0;
        }
        return option.schlName.toLowerCase().indexOf(value.toLowerCase()) === 0;
      });

      // push the 'not in list' option;
      if (this.notInList) {
        this.values.autocomplete.push(this.commonValues.autocomplete.notInList);
      }

      if (!this.notInList && this.values.autocomplete.length === 0) {
        this.values.autocomplete = [this.commonValues.autocomplete.noMatch];
      }

      this.show.autocomplete = true;
    }
    // if the form is less than X characters, show nothing - not even an error message;
    else {
      this.values.autocomplete = [];
    }
  }

  hideOptions(): void {
    this.show.autocomplete = false;
    this.highlightedItem = 0;
    this.lastAnnouncedAccessibleName = '';
  }

  // whenever an option of the form field is focused, clear any existing timeout, and show the autocomplete options;
  // so if the form field already has content in it on focus, options are shown;
  onFocus(): void {
    this.collapsedLiveText = '';
    window.clearTimeout(this.focusTimeout);
    this.showOptions();
  }

  onBlur(): void {
    this.collapsedLiveText = 'ComboBox Collapsed';
    this.focusTimeout = window.setTimeout(() => this.hideOptions(), this.focusTimeoutDelay);
  }

  selectOption(item): void {
    if (item) {
      // this.group.controls[this.controlName].setValue(item.schlName);
      this.onBlur();
      this.valueSelected.emit(item);
    }
  }

  isValueSelected(): boolean {
    if (!this.group || !this.controlName) {
      return false;
    }
    const control = this.group.controls[this.controlName];
    if (!control) {
      return false;
    }
    const value = control.value;
    if (!value || !this.options?.length) {
      return false;
    }
    return this.options.some(opt => opt?.schlName === value);
  }

  // hovering on an option needs to overwrite whatever item was selected with the arrow keys
  optionHoverHandler(e, i): void {
    this.highlightedItem = i;
  }

  // sets the highlighted state for options
  optionClass(i): string {
    if (i === this.highlightedItem) {
      return 'mat-selected';
    }
    return '';
  }

  // These two methods control scrolling. To make the scrolling work as expected, it gets a bit more
  // complicated than expected.
  scrollUpToOption(i): void {
    const itemHeight = this.optionList.nativeElement.firstElementChild.offsetHeight;
    const oldScroll = this.optionList.nativeElement.scrollTop;
    const newScroll = i * itemHeight;

    this.optionList.nativeElement.scrollTop = oldScroll > newScroll ? newScroll : oldScroll;
  }

  scrollDownToOption(i): void {
    const maxHeight = this.optionList.nativeElement.offsetHeight;
    const itemHeight = this.optionList.nativeElement.firstElementChild.offsetHeight;
    const oldScroll = this.optionList.nativeElement.scrollTop;
    const newScroll = (i - Math.floor(maxHeight / itemHeight) + 1) * itemHeight;

    this.optionList.nativeElement.scrollTop = oldScroll < newScroll ? newScroll : oldScroll;
  }

  // catch keyup events from the input field
  keyUpHandler(e): void {
    // Ignore modifier keys to prevent duplicate announcements when user types capital letters
    if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
      return;
    }

    if (this.show.autocomplete && this.values.autocomplete.length >= 1) {
      if (e.code === 'ArrowDown' || e.key === 'Down') {
        if (this.highlightedItem < this.values.autocomplete.length - 1) {
          this.highlightedItem++;
          if (this.highlightedItem > 4) {
            //window.setTimeout(() => this.scrollDownToOption(this.highlightedItem), 200);
          }
        }
      } else if (e.code === 'ArrowUp' || e.key === 'Up') {
        if (this.highlightedItem > 0) {
          this.highlightedItem--;
          if (this.highlightedItem < this.values.autocomplete.length - 5) {
            //window.setTimeout(() => this.scrollUpToOption(this.highlightedItem), 200);
          }
        }
      } else if (e.code === 'Enter' || e.key === 'Enter') {
        this.selectOption(this.values.autocomplete[this.highlightedItem]);
      } else {
        this.inputTimeout = window.setTimeout(() => {
          this.showOptions();
          this.announceHighlightedOption();
        }, this.inputTimeoutDelay);
      }
    } else {
      this.inputTimeout = window.setTimeout(() => {
        this.showOptions();
        this.announceHighlightedOption();
      }, this.inputTimeoutDelay);
    }
  }

  // Fill in the screen-reader announcement when mat-autocomplete's native one is
  // silent. 
  private announceHighlightedOption(): void {
    if (!this.show.autocomplete || !this.values.autocomplete?.length) {
      this.lastAnnouncedAccessibleName = '';
      return;
    }
    const first = this.values.autocomplete[0] as AutocompleteOption;
    const label = first?.schlName;
    if (!label) {
      return;
    }
    const controlValue = this.group?.controls?.[this.controlName]?.value;
    const isSelected = label === controlValue;
    const currentAccessibleName = isSelected ? `${label} selected` : label;

    if (currentAccessibleName !== this.lastAnnouncedAccessibleName) {
      this.lastAnnouncedAccessibleName = currentAccessibleName;
      return;
    }
    const state = isSelected ? 'selected' : 'not selected';
    const message = `${label}, ${state}, 1 of ${this.values.autocomplete.length}`;
    // Clear first so identical consecutive messages still fire in the live region.
    this.liveAnnouncer.clear();
    this.liveAnnouncer.announce(message, 'polite');
  }
}
 